import shared from "../../../shared/caller-languages.json";
import { callerLanguageName } from "@/constants/callerLanguages";

// Who hears the caller on a text model, config.transcriber.provider (owner, 2026-10-31; shared/agent-models.json,
// which transcribers.test.ts holds this file to): Gemini Transcribe Live, the default, or Deepgram Nova-3 in the
// agent's caller languages. 3.8 Live hears for itself and reads neither. The bot chooses Deepgram's model from the
// caller languages (pipecat/shared/deepgram_stt.py choose()); the helpers below say the same thing in the editor.
export const TRANSCRIBERS = ["gemini", "deepgram"] as const;
export type Transcriber = (typeof TRANSCRIBERS)[number];
export const DEFAULT_TRANSCRIBER: Transcriber = "gemini";

export const TRANSCRIBER_LABELS: Record<Transcriber, string> = {
  gemini: "Gemini Transcribe Live (default)",
  deepgram: "Deepgram",
};

type DeepgramCodes = { deepgram?: string; deepgram_multi?: string };
const LANGUAGES: ReadonlyArray<{ code: string } & DeepgramCodes> = shared.languages;
const codesOf = (code: string): DeepgramCodes => LANGUAGES.find((language) => language.code === code) ?? {};

/** How many languages Deepgram hears alone (its one-language model) and together (its multi-language model). */
export const DEEPGRAM_ALONE = LANGUAGES.filter((language) => language.deepgram).length;
export const DEEPGRAM_TOGETHER = LANGUAGES.filter((language) => language.deepgram_multi).length;

/** The line under the Transcriber dropdown, for what it is set to. */
export const TRANSCRIBER_HELP: Record<Transcriber, string> = {
  gemini: "Gemini Transcribe Live hears your callers in any language, detected on every reply.",
  deepgram:
    `Deepgram Nova-3 hears the languages you list below: any of its ${DEEPGRAM_ALONE} languages alone, or ` +
    `${DEEPGRAM_TOGETHER} of them together. One language is the most accurate.`,
};

/** Under Words to recognise while Deepgram is picked: it takes them as keyterms, up to a limit. */
export const DEEPGRAM_VOCABULARY_NOTE =
  "Deepgram listens for these too, up to its limit of about 500 tokens: a very long list is cut at the end.";

/** A stored config.transcriber as the dropdown shows it: its provider when it is one, else the default. */
export const transcriberOf = (stored: unknown): Transcriber => {
  const provider = stored && typeof stored === "object" ? (stored as { provider?: unknown }).provider : undefined;
  return (TRANSCRIBERS as readonly unknown[]).includes(provider) ? (provider as Transcriber) : DEFAULT_TRANSCRIBER;
};

/** A file's config.transcriber an agent can be created with: an object whose provider is unset or one of
 * TRANSCRIBERS (the server refuses any other). */
export const isKnownTranscriber = (value: unknown): boolean => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const provider = (value as { provider?: unknown }).provider;
  return provider === undefined || provider === null || provider === "" || (TRANSCRIBERS as readonly unknown[]).includes(provider);
};

const base = (code: string) => code.split("-")[0];
const heard = (code: string) => Boolean(codesOf(code).deepgram || codesOf(code).deepgram_multi);
const mixed = (code: string) => Boolean(codesOf(code).deepgram_multi);

/** The first code of each language listed: variants of one language (en-US, en-GB) count as one, the first used. */
const firsts = (codes: string[]): string[] => {
  const seen = new Map<string, string>();
  for (const code of codes) if (!seen.has(base(code))) seen.set(base(code), code);
  return [...seen.values()];
};

/** "A", "A and B", "A, B and C". */
const names = (codes: string[]): string => {
  const all = codes.map(callerLanguageName);
  return all.length === 1 ? all[0] : `${all.slice(0, -1).join(", ")} and ${all[all.length - 1]}`;
};

/** Why the picker cannot add `candidate` to `listed` while Deepgram is the transcriber; null when it can. */
export const deepgramBlock = (listed: string[], candidate: string): string | null => {
  if (!heard(candidate)) return "Deepgram can't hear it";
  const others = firsts(listed).filter((code) => base(code) !== base(candidate));
  const clash = mixed(candidate) ? others.filter((code) => heard(code) && !mixed(code)) : others;
  return clash.length ? `Deepgram can't hear it with ${names(clash)}` : null;
};

/** The note under one listed language while Deepgram is the transcriber; null for one it hears as listed. */
export const deepgramRowNote = (listed: string[], code: string): string | null => {
  const first = firsts(listed);
  if (!first.includes(code)) return null;
  if (!heard(code)) return `Deepgram can't hear ${callerLanguageName(code)}: calls use Gemini Transcribe Live.`;
  if (first.length > 1 && !mixed(code)) {
    return `Deepgram can't hear ${callerLanguageName(code)} together with other languages: calls use Gemini Transcribe Live.`;
  }
  return null;
};
